const SESSION_KEY = 'ts_an_sid';

/** Must match ANALYTICS_MAX_BATCH in @telesafi/shared. */
const MAX_BATCH = 50;

export type PageviewInput = {
  path: string;
  articleId?: string | null;
  categoryId?: string | null;
  authorId?: string | null;
  referrer?: string | null;
};

type QueuedEvent = Record<string, unknown> & { type: string };

export type TrackerOptions = {
  endpoint: string;
  siteSlug: string;
  now?: () => number;
  send?: (url: string, body: string) => void;
  storage?: Storage;
};

const defaultSend = (url: string, body: string) => {
  // sendBeacon survives the page unload that carries the exit event; fetch
  // with keepalive is the fallback where it is unavailable.
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    return;
  }
  void fetch(url, {
    method: 'POST',
    body,
    keepalive: true,
    headers: { 'content-type': 'application/json' },
  }).catch(() => {});
};

export function createTracker(opts: TrackerOptions) {
  const now = opts.now ?? (() => Date.now());
  const send = opts.send ?? defaultSend;
  const storage =
    opts.storage ?? (typeof sessionStorage !== 'undefined' ? sessionStorage : undefined);

  const existing = storage?.getItem(SESSION_KEY);
  const sessionId =
    existing ?? `s${now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  if (!existing) storage?.setItem(SESSION_KEY, sessionId);

  let queue: QueuedEvent[] = [];
  let current: PageviewInput | null = null;
  let lastPingAt = now();
  let maxScroll = 0;

  const push = (e: QueuedEvent) => {
    queue.push({ ...e, sessionId });
    if (queue.length >= MAX_BATCH) flush();
  };

  /** Closes out the page being left before a new one opens. */
  function closeCurrent() {
    if (!current) return;
    push({
      type: 'exit',
      path: current.path,
      articleId: current.articleId ?? null,
      maxScrollPct: maxScroll,
    });
    maxScroll = 0;
  }

  function pageview(input: PageviewInput) {
    closeCurrent();
    current = input;
    lastPingAt = now();
    push({
      type: 'pageview',
      path: input.path,
      articleId: input.articleId ?? null,
      categoryId: input.categoryId ?? null,
      authorId: input.authorId ?? null,
      referrer: input.referrer ?? null,
    });
  }

  /**
   * Engaged time is reported as a delta since the last ping, never a running
   * total, so the rollup can simply sum the column. A zero delta is dropped
   * rather than sent as a no-op row.
   */
  function ping() {
    if (!current) return;
    const t = now();
    const delta = t - lastPingAt;
    lastPingAt = t;
    if (delta <= 0) return;
    push({
      type: 'ping',
      path: current.path,
      articleId: current.articleId ?? null,
      engagedMs: delta,
    });
  }

  function scroll(pct: number) {
    if (pct > maxScroll) maxScroll = Math.max(0, Math.min(100, Math.round(pct)));
  }

  /** Drains the whole queue, in batches the collector will accept. */
  function flush() {
    while (queue.length > 0) {
      const events = queue.slice(0, MAX_BATCH);
      queue = queue.slice(MAX_BATCH);
      send(opts.endpoint, JSON.stringify({ siteSlug: opts.siteSlug, events }));
    }
  }

  function destroy() {
    closeCurrent();
    flush();
    current = null;
  }

  return { sessionId, pageview, ping, scroll, flush, destroy };
}

export type Tracker = ReturnType<typeof createTracker>;
